import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';

import { MenuItem, ModifierGroup, ModifierOption } from '../../core/models/menu.model';
import { AddCartItem, CartItem } from '../../core/models/cart.model';
import { DataService } from '../../core/services/data.service';
import { UiService } from '../../core/services/ui.service';
import { VARIANT_GROUP_ID } from '../../core/services/item.service';
import { ViewportService } from '../../core/services/viewport.service';
import {
  SheetComponent, ImgSlotComponent, IconComponent, TagComponent, PriceComponent,
  StepperComponent, SpinnerComponent, BtnComponent, TranslatePipe,
} from '../../shared';

@Component({
  selector: 'app-item-sheet',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, FormsModule, SheetComponent, ImgSlotComponent, IconComponent, TagComponent,
    PriceComponent, StepperComponent, SpinnerComponent, BtnComponent, TranslatePipe,
  ],
  templateUrl: './item-sheet.component.html',
  styleUrls: ['./item-sheet.component.css'],
})
export class ItemSheetComponent implements OnInit, OnDestroy {
  @ViewChild('scroll') scroll!: ElementRef<HTMLElement>;

  item: MenuItem | null = null;
  open = false;
  /** true while the item's details are being fetched (sheet shows a spinner) */
  loading = false;

  /** single: optionId|null ; multi (no qty): optionId[] */
  sel: Record<string, string | null | string[]> = {};
  /** multi-with-qty: { [optionId]: count } */
  qtyMap: Record<string, Record<string, number>> = {};
  qty = 1;
  note = '';
  tried = false;
  /** The cart line being edited (sheet saves over it instead of adding a new line). */
  editing: CartItem | null = null;
  allergensOpen = false;

  /** Weight items are sold by the kilo — quantity is a fraction of a kg. */
  readonly weightSteps: { v: number; label: string }[] = [
    { v: 0.125, label: '1/8' }, { v: 0.25, label: '1/4' }, { v: 1 / 3, label: '1/3' },
    { v: 0.5, label: '1/2' }, { v: 2 / 3, label: '2/3' }, { v: 0.75, label: '3/4' }, { v: 1, label: '1 kg' },
  ];
  get isWeight(): boolean { return this.item?.type === 'weight'; }
  /** Selected amount as a fraction label when it matches a preset, else "N kg". */
  get weightLabel(): string {
    const s = this.weightSteps.find((x) => Math.abs(x.v - this.qty) < 0.001);
    return s ? (s.label.includes('kg') ? s.label : s.label + ' kg') : `${this.qty} kg`;
  }
  setWeight(v: number): void { this.qty = v; }

  private sub?: Subscription;

  constructor(private data: DataService, private ui: UiService, private cdr: ChangeDetectorRef, public vp: ViewportService) {}

  ngOnInit(): void {
    this.sub = combineLatest([this.ui.itemSheet$, this.ui.itemLoading$]).subscribe(([item, loading]) => {
      if (item) {
        this.reset(item);
        this.editing = this.ui.editingLine;
        if (this.editing) this.prefill(this.editing);
      }
      this.loading = loading;
      this.open = loading || !!item;
      if (item) this.item = item;             // loaded item
      else if (loading) this.item = null;     // fresh load → show spinner, drop stale item
      // else: closing — keep this.item for the close animation
      this.cdr.markForCheck();
    });
  }
  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  private reset(item: MenuItem): void {
    const s: Record<string, string | null | string[]> = {};
    const qm: Record<string, Record<string, number>> = {};
    (item.mods || []).forEach((g) => {
      if (g.type === 'single') {
        // Preselect a Popular option, or the cheapest VARIANT (spec default). Required
        // modifier groups start UNSELECTED so the footer can show "Choose option".
        const popular = g.options.find((o) => o.popular && !o.soldOut);
        const preset = popular || (g.id === VARIANT_GROUP_ID ? g.options.find((o) => !o.soldOut) : undefined);
        s[g.id] = preset ? preset.id : null;
      } else if (g.qty) {
        qm[g.id] = {};
      } else {
        s[g.id] = [];
      }
    });
    this.item = item;
    this.sel = s;
    this.qtyMap = qm;
    this.qty = 1;
    this.note = '';
    this.tried = false;
    this.allergensOpen = false;
    if (this.scroll) this.scroll.nativeElement.scrollTop = 0;
  }

  /** Restore a cart line's variant, options, quantity and note onto the fresh sheet. */
  private prefill(line: CartItem): void {
    const sel = { ...this.sel };
    const qm = { ...this.qtyMap };
    const variants = this.groups.find((g) => g.id === VARIANT_GROUP_ID);
    if (variants?.options.some((o) => o.id === line.variantId && !o.soldOut)) sel[VARIANT_GROUP_ID] = line.variantId;
    // Required groups start empty; clear preset singles so only the line's picks show.
    this.groups.forEach((g) => { if (g.id !== VARIANT_GROUP_ID && g.type === 'single') sel[g.id] = null; });
    line.options.forEach((opt) => {
      // Options the menu no longer offers (or that sold out) are dropped silently.
      const g = this.groups.find((x) => x.id === opt.groupId && x.apiKind === opt.kind && x.options.some((o) => o.id === opt.id && !o.soldOut));
      if (!g) return;
      if (g.type === 'single') sel[g.id] = opt.id;
      else if (g.qty) qm[g.id] = { ...(qm[g.id] || {}), [opt.id]: opt.quantity || 1 };
      else {
        const cur = (sel[g.id] as string[]) || [];
        if (!cur.includes(opt.id) && cur.length < g.max) sel[g.id] = [...cur, opt.id];
      }
    });
    this.sel = sel;
    this.qtyMap = qm;
    this.qty = line.quantity || 1;
    this.note = line.note || '';
  }

  /** No size picked (e.g. every variant is sold out) — the add button stays disabled. */
  get variantMissing(): boolean {
    const g = this.groups.find((x) => x.id === VARIANT_GROUP_ID);
    if (!g) return false;
    const picked = g.options.find((o) => o.id === this.sel[g.id]);
    return !picked || !!picked.soldOut;
  }

  /** The whole item is sold out — nothing can be added. */
  get soldOut(): boolean { return !!this.item?.soldout; }
  /** The add button is disabled: sold out, or no size on sale is picked. */
  get addBlocked(): boolean { return this.soldOut || this.variantMissing; }

  /** Footer CTA label: sold out → pick missing options → save edit → add new. */
  get ctaLabel(): string {
    if (this.soldOut) return 'Sold out';
    return !this.valid ? 'Choose option' : this.editing ? 'Update item' : 'Add to cart';
  }

  get groups(): ModifierGroup[] { return this.item?.mods || []; }
  /** Calories for the currently-selected variant (falls back to the item's default). */
  get kcal(): number {
    const item = this.item;
    if (!item) return 0;
    const sel = this.sel[VARIANT_GROUP_ID];
    if (item.variantKcal && typeof sel === 'string' && sel in item.variantKcal) {
      return item.variantKcal[sel] ?? 0;
    }
    return item.kcal ?? 0;
  }
  get photo(): string | null {
    if (!this.item) return null;
    // Real API items carry their own photo URL; mock items use a generated image.
    return this.item.image ?? this.data.photoFor(this.item.id);
  }
  /** Header price — the selected variant's price (lowest/popular by default). */
  get headerPrice(): number {
    const item = this.item;
    if (!item) return 0;
    const sel = this.sel[VARIANT_GROUP_ID];
    if (typeof sel === 'string') {
      const o = this.groups.find((g) => g.id === VARIANT_GROUP_ID)?.options.find((x) => x.id === sel);
      if (o) return item.price + o.price;
    }
    return item.price;
  }

  isComplete(g: ModifierGroup): boolean {
    if (g.type === 'single') return !g.required || !!this.sel[g.id];
    if (g.qty) return !g.required || this.groupCount(g) >= (g.min || 1);
    return !g.required || (this.multiSel(g).length >= (g.min || 1));
  }
  get firstIncomplete(): ModifierGroup | undefined {
    return this.groups.find((g) => g.required && !this.isComplete(g));
  }
  get valid(): boolean { return !this.firstIncomplete; }

  get unit(): number {
    if (!this.item) return 0;
    let u = this.item.price;
    this.groups.forEach((g) => {
      if (g.type === 'single') {
        const o = g.options.find((x) => x.id === this.sel[g.id]);
        if (o) u += o.price;
      } else if (g.qty) {
        Object.entries(this.qtyMap[g.id] || {}).forEach(([oid, n]) => {
          const o = g.options.find((x) => x.id === oid);
          if (o) u += o.price * n;
        });
      } else {
        this.multiSel(g).forEach((oid) => {
          const o = g.options.find((x) => x.id === oid);
          if (o) u += o.price;
        });
      }
    });
    return u;
  }
  get total(): number { return this.unit * this.qty; }

  // ── selection helpers ──
  multiSel(g: ModifierGroup): string[] { return (this.sel[g.id] as string[]) || []; }
  groupCount(g: ModifierGroup): number {
    return Object.values(this.qtyMap[g.id] || {}).reduce((a, b) => a + b, 0);
  }
  optCount(g: ModifierGroup, o: ModifierOption): number { return (this.qtyMap[g.id] || {})[o.id] || 0; }
  isActive(g: ModifierGroup, o: ModifierOption): boolean {
    if (g.type === 'single') return this.sel[g.id] === o.id;
    if (g.qty) return this.optCount(g, o) > 0;
    return this.multiSel(g).includes(o.id);
  }
  groupIncomplete(g: ModifierGroup): boolean { return this.tried && g.required && !this.isComplete(g); }
  /**
   * The group's own rule, showing the minimum as well as the cap — "Choose up to 3"
   * alone hides that a required group also demands at least one.
   */
  groupSub(g: ModifierGroup): string {
    const min = g.required ? Math.max(1, g.min || 0) : (g.min || 0);
    const max = g.max || 0;
    if (!min && !max) return 'Optional';
    if (!min) return `Choose up to ${max}`;
    if (!max || max === min) return `Choose ${min}`;
    return `Choose ${min} to ${max}`;
  }

  onOptionClick(g: ModifierGroup, o: ModifierOption): void {
    if (o.soldOut) return;
    if (g.type === 'single') this.pickSingle(g, o.id);
    else if (!g.qty) this.toggleMulti(g, o.id);
  }
  /** Tapping the chosen option clears it when the group is optional; a required
   *  group must keep exactly one, so there the tap just re-selects. */
  pickSingle(g: ModifierGroup, oid: string): void {
    const clearing = !g.required && this.sel[g.id] === oid;
    this.sel = { ...this.sel, [g.id]: clearing ? '' : oid };
  }
  toggleMulti(g: ModifierGroup, oid: string): void {
    const cur = this.multiSel(g);
    if (cur.includes(oid)) this.sel = { ...this.sel, [g.id]: cur.filter((x) => x !== oid) };
    else if (cur.length < g.max) this.sel = { ...this.sel, [g.id]: [...cur, oid] };
  }
  setQtyOpt(g: ModifierGroup, o: ModifierOption, n: number): void {
    if (o.soldOut && n > this.optCount(g, o)) return;
    const cur = { ...(this.qtyMap[g.id] || {}) };
    const others = Object.values(cur).reduce((a, b) => a + b, 0) - (cur[o.id] || 0);
    if (n <= 0) delete cur[o.id];
    else if (others + n <= g.max) cur[o.id] = n;
    this.qtyMap = { ...this.qtyMap, [g.id]: cur };
  }

  close(): void { this.ui.closeItem(); }

  tryAdd(): void {
    if (!this.item || this.addBlocked) return;
    if (!this.valid) {
      this.tried = true;
      const g = this.firstIncomplete;
      if (g) {
        const el = this.scroll?.nativeElement.querySelector<HTMLElement>(`[data-gid="${g.id}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    // Resolve the chosen variant + option references for the cart API.
    const variantGroup = this.groups.find((g) => g.apiKind === 'variant');
    const variantId = (variantGroup ? (this.sel[variantGroup.id] as string) : this.item.apiVariantId) || '';

    const options: AddCartItem['options'] = [];
    this.groups.forEach((g) => {
      if (g.apiKind !== 'modifier' && g.apiKind !== 'addon') return;
      const kind = g.apiKind;
      const ids = g.type === 'single'
        ? (this.sel[g.id] ? [this.sel[g.id] as string] : [])
        : this.multiSel(g);
      ids.forEach((id) => options.push({ kind, id, groupId: g.id, ...(kind === 'addon' ? { quantity: 1 } : {}) }));
    });

    const variantName = variantGroup
      ? (variantGroup.options.find((o) => o.id === variantId)?.name ?? '')
      : '';
    // Human-readable labels of the chosen modifiers/add-ons (for the reservation summary).
    const optionLabels: string[] = [];
    this.groups.forEach((g) => {
      if (g.apiKind !== 'modifier' && g.apiKind !== 'addon') return;
      const ids = g.type === 'single'
        ? (this.sel[g.id] ? [this.sel[g.id] as string] : [])
        : g.qty ? Object.keys(this.qtyMap[g.id] || {}) : this.multiSel(g);
      ids.forEach((id) => { const o = g.options.find((x) => x.id === id); if (o) optionLabels.push(o.name); });
    });
    const req: AddCartItem = { menuItemId: this.item.id, variantId, quantity: this.qty, note: this.note || null, options };
    if (this.editing) { this.ui.updateCartLine(req, this.item.name); return; }
    this.ui.addConfiguredItem(
      req, this.item.name, this.unit, variantName, optionLabels,
    );
  }
}
